import {
  useEffect, useRef, useState, useCallback,
} from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  MyLocation,
  ZoomIn,
  ZoomOut,
  CenterFocusStrong,
} from '@mui/icons-material';
import maplibregl from 'maplibre-gl';
import MaplibreGeocoder from '@maplibre/maplibre-gl-geocoder';
import 'maplibre-gl/dist/maplibre-gl.css';
import '../../map/geocoder/geocoder.css';
import { useAttributePreference } from '../util/preferences';
import { useTranslation } from './LocalizationProvider';

// Default map style
const defaultMapStyle = 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json';

// Marker SVG as data URL (red pin)
const MARKER_SVG = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
  <path fill="#e91e63" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
  <circle fill="#fff" cx="12" cy="9" r="2.5"/>
</svg>
`)}`;

const MapLocationPicker = ({
  latitude,
  longitude,
  onChange,
  height = 300,
  zoom = 15,
  disabled = false,
}) => {
  const t = useTranslation();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const mapUrl = useAttributePreference('mapUrl', defaultMapStyle);
  const geocoderRef = useRef(null);

  const [mapReady, setMapReady] = useState(false);
  const [locating, setLocating] = useState(false);

  // Default center (Brazil) if no coordinates provided
  const defaultLat = latitude || -5.7945;
  const defaultLng = longitude || -35.211;

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: mapUrl,
      center: [defaultLng, defaultLat],
      zoom: latitude && longitude ? zoom : 4,
      attributionControl: false,
    });

    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');

    map.on('load', () => {
      setMapReady(true);
    });

    mapRef.current = map;

    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      map.remove();
      mapRef.current = null;
    };
  }, [mapUrl]);

  // Add Geocoder control
  useEffect(() => {
    if (!mapReady || !mapRef.current || disabled) return;

    const map = mapRef.current;

    // Create geocoder with Nominatim API
    const geocoderApi = {
      forwardGeocode: async (config) => {
        const features = [];
        try {
          const request = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(config.query)}&format=geojson&polygon_geojson=1&addressdetails=1`;
          const response = await fetch(request);
          const geojson = await response.json();
          geojson.features.forEach((feature) => {
            const center = [
              feature.bbox[0] + (feature.bbox[2] - feature.bbox[0]) / 2,
              feature.bbox[1] + (feature.bbox[3] - feature.bbox[1]) / 2,
            ];
            features.push({
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: center,
              },
              place_name: feature.properties.display_name,
              properties: feature.properties,
              text: feature.properties.display_name,
              place_type: ['place'],
              center,
            });
          });
        } catch (e) {
          console.error('Geocoder error:', e);
        }
        return { features };
      },
    };

    const geocoder = new MaplibreGeocoder(geocoderApi, {
      maplibregl,
      collapsed: true,
      placeholder: t('mapSearch') || 'Buscar endereço...',
      marker: false, // We handle marker ourselves
    });

    // Handle geocoder result
    geocoder.on('result', (e) => {
      if (e.result && e.result.center) {
        const [lng, lat] = e.result.center;
        onChange({
          latitude: parseFloat(lat.toFixed(6)),
          longitude: parseFloat(lng.toFixed(6)),
        });
      }
    });

    map.addControl(geocoder, 'top-left');
    geocoderRef.current = geocoder;

    return () => {
      if (geocoderRef.current && map) {
        try {
          map.removeControl(geocoderRef.current);
        } catch (e) {
          // Control may already be removed
        }
        geocoderRef.current = null;
      }
    };
  }, [mapReady, disabled, onChange, t]);

  // Create/update marker when coordinates change
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;

    const map = mapRef.current;

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }

    // Only create marker if we have valid coordinates
    if (latitude && longitude && !isNaN(latitude) && !isNaN(longitude)) {
      // Create custom marker element
      const el = document.createElement('div');
      el.style.width = '40px';
      el.style.height = '40px';
      el.style.backgroundImage = `url("${MARKER_SVG}")`;
      el.style.backgroundSize = 'contain';
      el.style.backgroundRepeat = 'no-repeat';
      el.style.cursor = disabled ? 'default' : 'grab';

      const marker = new maplibregl.Marker({
        element: el,
        anchor: 'bottom',
        draggable: !disabled,
      })
        .setLngLat([longitude, latitude])
        .addTo(map);

      // Handle marker drag
      if (!disabled) {
        marker.on('dragend', () => {
          const lngLat = marker.getLngLat();
          onChange({
            latitude: parseFloat(lngLat.lat.toFixed(6)),
            longitude: parseFloat(lngLat.lng.toFixed(6)),
          });
        });
      }

      markerRef.current = marker;

      // Center map on marker
      map.flyTo({
        center: [longitude, latitude],
        zoom: map.getZoom() < 10 ? zoom : map.getZoom(),
        duration: 500,
      });
    }
  }, [mapReady, latitude, longitude, disabled, onChange, zoom]);

  // Handle map click to set location
  useEffect(() => {
    if (!mapReady || !mapRef.current || disabled) return;

    const map = mapRef.current;

    const handleClick = (e) => {
      const { lng, lat } = e.lngLat;
      onChange({
        latitude: parseFloat(lat.toFixed(6)),
        longitude: parseFloat(lng.toFixed(6)),
      });
    };

    map.on('click', handleClick);

    // Change cursor to crosshair
    map.getCanvas().style.cursor = 'crosshair';

    return () => {
      map.off('click', handleClick);
      if (map.getCanvas()) {
        map.getCanvas().style.cursor = '';
      }
    };
  }, [mapReady, disabled, onChange]);

  // Get current location
  const handleGetLocation = useCallback(() => {
    if (!navigator.geolocation || disabled) return;

    setLocating(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        onChange({
          latitude: parseFloat(position.coords.latitude.toFixed(6)),
          longitude: parseFloat(position.coords.longitude.toFixed(6)),
        });
        setLocating(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        setLocating(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, [disabled, onChange]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.zoomIn();
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.zoomOut();
    }
  }, []);

  // Center on marker
  const handleCenter = useCallback(() => {
    if (mapRef.current && latitude && longitude) {
      mapRef.current.flyTo({
        center: [longitude, latitude],
        zoom: zoom,
        duration: 500,
      });
    }
  }, [latitude, longitude, zoom]);

  return (
    <Paper
      variant="outlined"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 1,
      }}
    >
      {/* Map Container */}
      <Box
        ref={mapContainerRef}
        sx={{
          width: '100%',
          height,
          opacity: disabled ? 0.6 : 1,
        }}
      />

      {/* Controls Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        <Tooltip title={t('mapLiveRoutes') || 'Minha Localização'} placement="left">
          <IconButton
            size="small"
            onClick={handleGetLocation}
            disabled={disabled || locating}
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            <MyLocation fontSize="small" color={locating ? 'disabled' : 'primary'} />
          </IconButton>
        </Tooltip>

        <Tooltip title={t('mapZoomIn') || 'Zoom +'} placement="left">
          <IconButton
            size="small"
            onClick={handleZoomIn}
            disabled={disabled}
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            <ZoomIn fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={t('mapZoomOut') || 'Zoom -'} placement="left">
          <IconButton
            size="small"
            onClick={handleZoomOut}
            disabled={disabled}
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            <ZoomOut fontSize="small" />
          </IconButton>
        </Tooltip>

        {latitude && longitude && (
          <Tooltip title={t('mapCenter') || 'Centralizar'} placement="left">
            <IconButton
              size="small"
              onClick={handleCenter}
              disabled={disabled}
              sx={{
                bgcolor: 'background.paper',
                boxShadow: 1,
                '&:hover': { bgcolor: 'background.paper' },
              }}
            >
              <CenterFocusStrong fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Instructions Overlay */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          right: 8,
          bgcolor: 'rgba(0,0,0,0.6)',
          color: 'white',
          borderRadius: 1,
          px: 1.5,
          py: 0.5,
        }}
      >
        <Typography variant="caption">
          {disabled
            ? t('mapDisabled') || 'Mapa desabilitado'
            : t('mapClickToSelect') || 'Clique no mapa para selecionar a localização ou arraste o marcador'}
        </Typography>
      </Box>

      {/* Coordinates Display - positioned below geocoder */}
      {latitude && longitude && (
        <Box
          sx={{
            position: 'absolute',
            top: 44,
            left: 8,
            bgcolor: 'rgba(0,0,0,0.6)',
            color: 'white',
            borderRadius: 1,
            px: 1.5,
            py: 0.5,
            zIndex: 1,
          }}
        >
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
            {latitude.toFixed(6)}, {longitude.toFixed(6)}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default MapLocationPicker;
